import { api } from './api';
import type { ClientEngagement, ClientExerciseLink } from '@/types';
import type { FrameworkAssessment, RequirementResponse } from '@/types/framework';
import type { EvidenceDocument } from '@/services/assessments.api';

/** One lifecycle transition, as the client is allowed to see it. */
export interface PortalStatusEvent {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  occurredAt: string;
  /** Mandatory on a send-back — this is the assessor saying what is needed. */
  reason: string | null;
  actorName: string;
}

/** A requirement the assessor is waiting on this client for. */
export interface PortalAwaitingRequirement {
  responseId: string;
  requirementId: string;
  code: string;
  title: string;
  assessorNote: string | null;
  since: string | null;
}

// ── Portal API — used by client users only ───────────────────────────────────

export interface PortalDashboard {
  organisation: {
    id: string;
    name: string;
    shortName: string;
    logoUrl?: string | null;
    showPortalEngagements?: boolean;
    showPortalExercises?: boolean;
  };
  portalNotificationsEnabled?: boolean;
  /** True when a Trust Centre pack has been granted to this organisation. */
  portalTrustCentreEnabled?: boolean;
  stats: {
    totalEngagements: number;
    activeEngagements: number;
    completedEngagements: number;
    pendingExercises: number;
  };
  recentEngagements: ClientEngagement[];
}

export interface PortalRequirement {
  id: string;
  controlId: string;
  control?: { id: string; code: string; title: string };
  status: string;
  notes?: string;
  responses?: unknown[];
}

export interface PortalEvidence {
  id: string;
  fileName: string;
  fileSize: number;
  uploadedById: string;
  uploadedBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

const P = '/portal';

export const portalApi = {
  getDashboard: () =>
    api.get<{ data: PortalDashboard }>(`${P}/dashboard`).then(r => r.data.data),

  listEngagements: (params?: Record<string, string>) =>
    api.get<{ data: { data: ClientEngagement[]; meta: unknown } }>(`${P}/engagements`, { params }).then(r => {
      const payload = r.data.data;
      return Array.isArray(payload) ? payload : (payload as any).data ?? [];
    }),

  getEngagement: (id: string) =>
    api.get<{ data: ClientEngagement }>(`${P}/engagements/${id}`).then(r => r.data.data),

  getEngagementReport: (id: string) =>
    api.get<{ data: import('@/types/framework').EngagementReport }>(`${P}/engagements/${id}/report`).then(r => r.data.data),

  getRequirements: (engagementId: string, params?: Record<string, string>) =>
    api.get<{ data: PortalRequirement[] }>(`${P}/engagements/${engagementId}/requirements`, { params }).then(r => r.data.data),

  getEvidence: (engagementId: string) =>
    api.get<{ data: PortalEvidence[] }>(`${P}/engagements/${engagementId}/evidence`).then(r => r.data.data),

  listExercises: () =>
    api.get<{ data: ClientExerciseLink[] }>(`${P}/exercises`).then(r => r.data.data),

  getExercise: (id: string) =>
    api.get<{ data: ClientExerciseLink }>(`${P}/exercises/${id}`).then(r => r.data.data),

  getProfile: () =>
    api.get<{ data: unknown }>(`${P}/profile`).then(r => r.data.data),

  listNotifications: (params?: Record<string, string>) =>
    api.get<{ data: unknown[] }>(`${P}/notifications`, { params }).then(r => r.data.data),

  // ── Assessments (per-engagement framework assessments) ────────────────────
  getAssessment: (id: string) =>
    api.get<{ data: FrameworkAssessment }>(`${P}/assessments/${id}`).then(r => r.data.data),

  getAssessmentResponses: (id: string) =>
    api.get<{ data: RequirementResponse[] }>(`${P}/assessments/${id}/responses`).then(r => r.data.data),

  /** Lifecycle history — including why the assessor sent it back. */
  getAssessmentHistory: (id: string) =>
    api.get<{ data: PortalStatusEvent[] }>(`${P}/assessments/${id}/history`).then(r => r.data.data),

  /** Requirements the assessor is waiting on, with their note on each. */
  getAwaitingRequirements: (id: string) =>
    api.get<{ data: PortalAwaitingRequirement[] }>(`${P}/assessments/${id}/awaiting`).then(r => r.data.data),

  submitAssessmentResponse: (
    assessmentId: string,
    requirementId: string,
    body: { status: string; maturityLevel?: string; notes?: string; evidence?: string },
  ) =>
    api
      .put<{ data: RequirementResponse }>(
        `${P}/assessments/${assessmentId}/responses/${requirementId}`,
        body,
      )
      .then(r => r.data.data),

  listAssessmentEvidence: (assessmentId: string, requirementId: string) =>
    api
      .get<{ data: EvidenceDocument[] }>(
        `${P}/assessments/${assessmentId}/responses/${requirementId}/evidence`,
      )
      .then(r => r.data.data),

  uploadAssessmentEvidence: (assessmentId: string, requirementId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post<{ data: EvidenceDocument }>(
        `${P}/assessments/${assessmentId}/responses/${requirementId}/evidence`,
        form,
      )
      .then(r => r.data.data);
  },

  deleteAssessmentEvidence: (assessmentId: string, requirementId: string, docId: string) =>
    api.delete(`${P}/assessments/${assessmentId}/responses/${requirementId}/evidence/${docId}`),

  /** Fetch an evidence file as a blob (Bearer-authenticated) and return an object URL. */
  openAssessmentEvidence: async (assessmentId: string, requirementId: string, docId: string) => {
    const res = await api.get(
      `${P}/assessments/${assessmentId}/responses/${requirementId}/evidence/${docId}/file`,
      { responseType: 'blob' },
    );
    return URL.createObjectURL(res.data as Blob);
  },
};
